import { useEffect, useRef, useState } from 'react';
import './useSortableList.css';

const TRANSITION = 'transform 180ms cubic-bezier(0.2, 0, 0, 1)';

/**
 * Vertical drag-and-drop reordering where the other items slide apart to open
 * a gap at the drop position. Spread `containerProps` on the list element,
 * `itemProps(index)` on each direct child item and `handleProps(index)` on the
 * drag handle (which may be the item itself).
 */
export default function useSortableList({ onMove, disabled = false }) {
  const containerRef = useRef(null);
  const dragRef = useRef(null);
  const settleTimer = useRef();
  const onMoveRef = useRef(onMove);
  const [drag, setDrag] = useState(null);
  onMoveRef.current = onMove;

  useEffect(() => () => window.clearTimeout(settleTimer.current), []);

  const itemElements = () => {
    const container = containerRef.current;
    if (!container) return [];
    return Array.from(container.children)
      .filter(element => element.dataset?.sortableIndex !== undefined)
      .sort((a, b) => Number(a.dataset.sortableIndex) - Number(b.dataset.sortableIndex));
  };

  const start = (index, event) => {
    const container = containerRef.current;
    if (disabled || !container) return;
    const base = container.getBoundingClientRect().top - container.scrollTop;
    const elements = itemElements();
    const layout = elements.map((element) => {
      const rect = element.getBoundingClientRect();
      return { top: rect.top - base, bottom: rect.bottom - base };
    });
    const self = layout[index];
    if (!self) return;

    event.dataTransfer.effectAllowed = 'move';
    // Firefox requires setData() during dragstart, otherwise the drag never starts.
    event.dataTransfer.setData('text/plain', String(index));
    const item = elements[index];
    if (item && item !== event.currentTarget && typeof event.dataTransfer.setDragImage === 'function') {
      const rect = item.getBoundingClientRect();
      event.dataTransfer.setDragImage(item, event.clientX - rect.left, event.clientY - rect.top);
    }

    const state = { from: index, to: index, layout };
    dragRef.current = state;
    window.clearTimeout(settleTimer.current);
    // Deferred so the browser captures the drag image before the placeholder styling is applied.
    window.setTimeout(() => { if (dragRef.current === state) setDrag(state); }, 0);
  };

  const over = (event) => {
    const state = dragRef.current;
    const container = containerRef.current;
    if (!state || !container) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    const y = event.clientY - container.getBoundingClientRect().top + container.scrollTop;
    const to = state.layout.reduce((count, slot, index) => (
      index !== state.from && (slot.top + slot.bottom) / 2 < y ? count + 1 : count
    ), 0);
    if (to !== state.to) {
      dragRef.current = { ...state, to };
      setDrag(dragRef.current);
    }
  };

  const drop = (event) => {
    const state = dragRef.current;
    if (!state) return;
    event.preventDefault();
    dragRef.current = null;
    setDrag(null);
    if (!disabled && state.to !== state.from) onMoveRef.current(state.from, state.to);
  };

  const end = () => {
    const state = dragRef.current;
    if (!state) return;
    dragRef.current = null;
    if (state.to === state.from) {
      setDrag(null);
      return;
    }
    setDrag({ ...state, to: state.from });
    settleTimer.current = window.setTimeout(() => setDrag(null), 200);
  };

  const itemProps = (index) => {
    if (!drag) return { 'data-sortable-index': index };
    const { from, to, layout } = drag;
    // Rebuild the preview using each row's height and each position's gap.
    const order = layout.map((_, slotIndex) => slotIndex);
    order.splice(to, 0, order.splice(from, 1)[0]);
    let top = layout[0].top;
    for (let position = 0; position < order.length; position += 1) {
      const slotIndex = order[position];
      if (slotIndex === index) break;
      const slot = layout[slotIndex];
      const gap = layout[position + 1] ? layout[position + 1].top - layout[position].bottom : 0;
      top += slot.bottom - slot.top + gap;
    }
    const offset = top - layout[index].top;
    return {
      'data-sortable-index': index,
      'data-sortable-state': index === from ? 'placeholder' : 'shifting',
      style: { transition: TRANSITION, transform: offset ? `translate3d(0, ${offset}px, 0)` : undefined },
    };
  };

  const handleProps = index => ({
    draggable: !disabled,
    onDragStart: event => start(index, event),
    onDragEnd: end,
  });

  return {
    containerProps: { ref: containerRef, onDragOver: over, onDrop: drop },
    itemProps,
    handleProps,
    isDragging: drag !== null,
  };
}
